import { useEffect, useState } from 'react';
import { Button as AriaButton, Disclosure, DisclosurePanel, Heading } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { getChildrenByLineID } from '@/api/getChildByLineID';
// import { UpdateChildRequest } from '@/api/updateChild';
import { updateChildWithLineID, UpdateChildRequest } from '@/api/updateChildByLineID';
import Button from '@/components/Button/Button';
import BasicInfoForm from '@/components/MyPage/BasicInfoForm';
import ExternalContractsForm from '@/components/MyPage/ExternalContractsForm';
import OtherForm from '@/components/MyPage/OtherForm';
import RecipientCertificate from '@/components/MyPage/RecipientCertificate';
import { LineUserAtom } from '@/jotai/jotai';
import { fileToBase64 } from '@/logics/base64/base64';
import { RelationShip, schools, validateRelationShip } from '@/logics/children/children';
import { formatDate } from '@/logics/date/date';
import { isValidDate, isValidNumber, isValidString } from '@/logics/validators/validators';
import { Child } from '@/types/child';

export type UpdateChildFormData = {
  contractStartDate: Date;
  contractEndDate?: Date;
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  birthDate: Date;
  guardianId1: number;
  guardianRelationship1: RelationShip;
  guardianId2?: number;
  guardianRelationship2?: RelationShip;
  guardianId3?: number;
  guardianRelationship3?: RelationShip;
  guardianId4?: number;
  guardianRelationship4?: RelationShip;
  email: string;
  phoneNumber: string;
  postalCode: string;
  prefecture: string;
  city: string;
  address1: string;
  address2?: string;
  emergencyContactRelation: string;
  emergencyContactPhone: string;
  schoolId: number;
  schoolPhoneNumber?: string;
  schoolGrade: string;
  doctorClinicName1?: string;
  doctorName1?: string;
  doctorPhoneNumber1?: string;
  doctorClinicName2?: string;
  doctorName2?: string;
  doctorPhoneNumber2?: string;
  doctorClinicName3?: string;
  doctorName3?: string;
  doctorPhoneNumber3?: string;
  hasUpperManagement: boolean;
  otherFacilityName1?: string;
  otherFacilityCity1?: string;
  otherFacilityName2?: string;
  otherFacilityCity2?: string;
  recipientCertificateStartDate1: Date;
  recipientCertificateEndDate1: Date;
  recipientCertificateStartDate2?: Date;
  recipientCertificateEndDate2?: Date;
  recipientCertificateNumber: number;
  supportType: string;
  supportDays: string;
  maxMonthlyBurdenAmount: number;
  recipientCertificateNotes?: string;
  recipientCertificateGuardianName?: string;
  recipientCertificateGuardianBirthDate?: Date;
  disabilityDetails?: string;
  allergies?: string;
  trainingLocation?: string;
  notes?: string;
};

const UserPage = () => {
  const [child, setChild] = useState<Child>();
  const [lineUser, _] = useAtom(LineUserAtom);
  const [sealRegistrationFile, setSealRegistrationFile] = useState<File>();
  const [recipientCertificateFile1, setRecipientCertificateFile1] = useState<File>();
  const [recipientCertificateFile2, setRecipientCertificateFile2] = useState<File>();
  const [rehabilitationCertificateFile, setRehabilitationCertificateFile] = useState<File>();
  const [recipientCertificateIsDeleted1, setRecipientCertificateIsDeleted1] =
    useState<boolean>(false);
  const [recipientCertificateIsDeleted2, setRecipientCertificateIsDeleted2] =
    useState<boolean>(false);
  const [recipientCertificateIsLocked1, setRecipientCertificateIsLocked1] = useState<boolean>(
    (child?.recipient_certificates && child.recipient_certificates[0].is_locked) || false
  );
  const [recipientCertificateIsLocked2, setRecipientCertificateIsLocked2] = useState<boolean>(
    child?.recipient_certificates && child?.recipient_certificates.length > 1
      ? child.recipient_certificates[1].is_locked || false
      : false
  );

  useEffect(() => {
    const getChild = async () => {
      const response = await getChildrenByLineID(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? ''
      );
      if (response.status === 200) {
        setChild(response.result[0]);
        if (
          response.result[0].recipient_certificates &&
          response.result[0].recipient_certificates.length > 0
        ) {
          setRecipientCertificateIsLocked1(
            response.result[0].recipient_certificates[0].is_locked || false
          );
        }
        if (
          response.result[0].recipient_certificates &&
          response.result[0].recipient_certificates.length > 1
        ) {
          setRecipientCertificateIsLocked2(
            response.result[0].recipient_certificates[1].is_locked || false
          );
        }
        reset(childToFormData(response.result[0]));
      }
    };

    if (lineUser) {
      getChild();
    }
  }, [lineUser]);

  const id = Number(child?.id);

  const schema: yup.ObjectSchema<UpdateChildFormData> = yup
    .object({
      contractStartDate: yup.date().required('利用契約開始日は必須です'),
      contractEndDate: yup.date().optional(),
      lastName: yup.string().required('利用者（児童）（姓）は必須です'),
      firstName: yup.string().required('利用者（児童）（名）は必須です'),
      lastNameKana: yup.string().required('利用者（児童）（セイ）は必須です'),
      firstNameKana: yup.string().required('利用者（児童）（メイ）は必須です'),
      birthDate: yup.date().required('生年月日は必須です'),
      guardianId1: yup.number().required('保護者_1は必須です'),
      guardianRelationship1: yup
        .mixed<RelationShip>()
        .oneOf(['father', 'mother', 'grandfather', 'grandmother', 'uncle', 'aunt', 'other'])
        .required('保護者_1（関係性）は必須です'),
      guardianId2: yup.number().optional(),
      guardianRelationship2: yup
        .mixed<RelationShip>()
        .oneOf(['father', 'mother', 'grandfather', 'grandmother', 'uncle', 'aunt', 'other'])
        .optional(),
      guardianId3: yup.number().optional(),
      guardianRelationship3: yup
        .mixed<RelationShip>()
        .oneOf(['father', 'mother', 'grandfather', 'grandmother', 'uncle', 'aunt', 'other'])
        .optional(),
      guardianId4: yup.number().optional(),
      guardianRelationship4: yup
        .mixed<RelationShip>()
        .oneOf(['father', 'mother', 'grandfather', 'grandmother', 'uncle', 'aunt', 'other'])
        .optional(),
      email: yup
        .string()
        .email('メールアドレスの形式が正しくありません')
        .required('メールアドレスは必須です'),
      phoneNumber: yup.string().required('連絡先TELは必須です'),
      postalCode: yup.string().required('郵便番号は必須です'),
      prefecture: yup.string().required('都道府県は必須です'),
      city: yup.string().required('市区町村は必須です'),
      address1: yup.string().required('住所1は必須です'),
      address2: yup.string().optional(),
      emergencyContactRelation: yup.string().required('緊急連絡先（関係性）は必須です'),
      emergencyContactPhone: yup.string().required('緊急連絡先TELは必須です'),
      first_name_kana: yup.string().optional(),
      phone_number: yup.string().optional(),
      schoolId: yup.number().required('学校は必須です'),
      schoolPhoneNumber: yup.string().required('学校TELは必須です'),
      schoolGrade: yup.string().required('学年は必須です'),
      doctorClinicName1: yup.string().optional(),
      doctorPhoneNumber1: yup.string().optional(),
      doctorName1: yup.string().optional(),
      doctorClinicName2: yup.string().optional(),
      doctorPhoneNumber2: yup.string().optional(),
      doctorName2: yup.string().optional(),
      doctorClinicName3: yup.string().optional(),
      doctorPhoneNumber3: yup.string().optional(),
      doctorName3: yup.string().optional(),
      hasUpperManagement: yup.boolean().required('上限管理は必須です'),
      otherFacilityName1: yup.string().optional(),
      otherFacilityCity1: yup.string().optional(),
      otherFacilityName2: yup.string().optional(),
      otherFacilityCity2: yup.string().optional(),
      recipientCertificateStartDate1: yup.date().required('受給者証（1）/適用期間開始は必須です'),
      recipientCertificateEndDate1: yup.date().required('受給者証（1）/適用期間終了は必須です'),
      recipientCertificateStartDate2: yup.date().optional(),
      recipientCertificateEndDate2: yup.date().optional(),
      recipientCertificateNumber: yup.number().required('受給者証番号は必須です'),
      supportType: yup.string().required('支援の種類は必須です'),
      supportDays: yup.string().required('支給量等は必須です'),
      maxMonthlyBurdenAmount: yup.number().required('負担上限額は必須です'),
      recipientCertificateNotes: yup.string().optional(),
      recipientCertificateGuardianName: yup.string().optional(),
      recipientCertificateGuardianBirthDate: yup.date().optional(),
      disabilityDetails: yup.string().optional(),
      allergies: yup.string().optional(),
      trainingLocation: yup.string().optional(),
      notes: yup.string().optional(),
    })
    .required();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { isValid, errors },
  } = useForm<UpdateChildFormData>({
    defaultValues: child ? childToFormData(child) : {},
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<UpdateChildFormData> = async (data) => {
    const input: UpdateChildRequest = {
      contract_start_date: formatDate(data.contractStartDate),
      contract_end_date: data.contractEndDate ? formatDate(data.contractEndDate) : undefined,
      last_name: data.lastName,
      first_name: data.firstName,
      last_name_kana: data.lastNameKana,
      first_name_kana: data.firstNameKana,
      birth_date: formatDate(data.birthDate),
      guardians: [
        {
          id: Number(data.guardianId1),
          relation_ship: data.guardianRelationship1,
        },
      ],
      email: data.email,
      phone_number: data.phoneNumber,
      postal_code: data.postalCode,
      prefecture: data.prefecture,
      city: data.city,
      address1: data.address1,
      address2: data.address2,
      emergency_contact_relation: data.emergencyContactRelation,
      emergency_contact_phone: data.emergencyContactPhone,
      school_id: Number(data.schoolId),
      school_phone_number: data.schoolPhoneNumber,
      school_grade: data.schoolGrade,
      doctors: [],
      has_upper_management: data.hasUpperManagement,
      other_facilities: [],
      seal_registration_image: sealRegistrationFile
        ? await fileToBase64(sealRegistrationFile)
        : child?.seal_registration_image_url || undefined,
      recipient_certificates: [
        {
          image: recipientCertificateFile1
            ? await fileToBase64(recipientCertificateFile1)
            : (child?.recipient_certificates &&
                child?.recipient_certificates.length &&
                child?.recipient_certificates?.[0].image_url) ||
              undefined,
          start_date: formatDate(data.recipientCertificateStartDate1),
          end_date: formatDate(data.recipientCertificateEndDate1),
          is_locked: recipientCertificateIsLocked1,
          is_deleted: recipientCertificateIsDeleted1,
        },
      ],
      recipient_certificate_number: data.recipientCertificateNumber,
      support_type: data.supportType,
      support_days: data.supportDays,
      max_monthly_burden_amount: data.maxMonthlyBurdenAmount,
      recipient_certificate_notes: data.recipientCertificateNotes,
      recipient_certificate_guardian_name: data.recipientCertificateGuardianName,
      recipient_certificate_guardian_birth_date: data.recipientCertificateGuardianBirthDate
        ? formatDate(data.recipientCertificateGuardianBirthDate)
        : undefined,
      rehabilitation_certificate_image: rehabilitationCertificateFile
        ? await fileToBase64(rehabilitationCertificateFile)
        : child?.rehabilitation_certificate_image_url || undefined,
      disability_details: data.disabilityDetails,
      allergies: data.allergies,
      training_location: data.trainingLocation,
      notes: data.notes,
    };
    // 保護者情報が入力されていれば追加
    if (isValidNumber(Number(data.guardianId2)) && data.guardianRelationship2 !== undefined) {
      input.guardians[1] = {
        id: Number(data.guardianId2),
        relation_ship: data.guardianRelationship2,
      };
    }
    if (isValidNumber(Number(data.guardianId3)) && data.guardianRelationship3 !== undefined) {
      input.guardians[2] = {
        id: Number(data.guardianId3),
        relation_ship: data.guardianRelationship3,
      };
    }
    if (isValidNumber(Number(data.guardianId4)) && data.guardianRelationship4 !== undefined) {
      input.guardians[3] = {
        id: Number(data.guardianId4),
        relation_ship: data.guardianRelationship4,
      };
    }
    // かかりつけ医情報が入力されていれば追加
    if (
      isValidString(data.doctorClinicName1) ||
      isValidString(data.doctorName1) ||
      isValidString(data.doctorPhoneNumber1)
    ) {
      input.doctors.push({
        number: 1,
        clinic_name: data.doctorClinicName1,
        doctor_name: data.doctorName1,
        phone_number: data.doctorPhoneNumber1,
      });
    }
    if (
      isValidString(data.doctorClinicName2) ||
      isValidString(data.doctorName2) ||
      isValidString(data.doctorPhoneNumber2)
    ) {
      input.doctors.push({
        number: 2,
        clinic_name: data.doctorClinicName2,
        doctor_name: data.doctorName2,
        phone_number: data.doctorPhoneNumber2,
      });
    }
    if (
      isValidString(data.doctorClinicName3) ||
      isValidString(data.doctorName3) ||
      isValidString(data.doctorPhoneNumber3)
    ) {
      input.doctors.push({
        number: 3,
        clinic_name: data.doctorClinicName3,
        doctor_name: data.doctorName3,
        phone_number: data.doctorPhoneNumber3,
      });
    }
    // その他事業所情報が入力されていれば追加
    if (isValidString(data.otherFacilityName1) || isValidString(data.otherFacilityCity1)) {
      input.other_facilities.push({
        number: 1,
        name: data.otherFacilityName1,
        city: data.otherFacilityCity1,
      });
    }
    if (isValidString(data.otherFacilityName2) || isValidString(data.otherFacilityCity2)) {
      input.other_facilities.push({
        number: 2,
        name: data.otherFacilityName2,
        city: data.otherFacilityCity2,
      });
    }
    // 受給者証情報が入力されていれば追加
    if (
      isValidDate(data.recipientCertificateStartDate2) &&
      isValidDate(data.recipientCertificateEndDate2)
    ) {
      input.recipient_certificates.push({
        image: recipientCertificateFile2
          ? await fileToBase64(recipientCertificateFile2)
          : (child?.recipient_certificates &&
              child?.recipient_certificates.length &&
              child?.recipient_certificates?.[1].image_url) ||
            undefined,
        start_date: formatDate(data.recipientCertificateStartDate2),
        end_date: formatDate(data.recipientCertificateEndDate2),
        is_locked: recipientCertificateIsLocked2,
        is_deleted: recipientCertificateIsDeleted2,
      });
    }

    const result = await updateChildWithLineID(
      lineUser?.lineID ?? '',
      lineUser?.accessToken ?? '',
      id,
      input
    );
    console.log(result);
    if (result.status === 200) {
      alert('保存しました');
    } else {
      console.log('error');
    }
  };

  return (
    <div className="mx-4 text-[22px]">
      <div className="my-4 font-bold">マイページ</div>
      <form
        onSubmit={handleSubmit(onSubmit, (errors) => {
          console.log('Validation errors:', errors);
        })}
      >
        <div className="space-y-4">
          <Disclosure className="h-full rounded-md bg-gray-100">
            <Heading className="">
              <AriaButton
                className="flex w-full flex-row items-center justify-between p-4 py-4"
                slot="trigger"
              >
                <div className="text-lg font-bold">基本情報</div>
                <svg
                  width="16"
                  height="17"
                >
                  <g>
                    <path
                      d="M8.0208 11.9886L2.03662 5.92192L2.70153 5.25525L8.0208 10.5886L13.3401 5.25525L14.005 5.92192L8.0208 11.9886Z"
                      fill="#1A1A1A"
                    />
                  </g>
                </svg>
              </AriaButton>
            </Heading>
            <DisclosurePanel>
              <BasicInfoForm
                child={child}
                errors={errors}
                register={register}
                control={control}
              />
            </DisclosurePanel>
          </Disclosure>
          <Disclosure className="h-full rounded-md bg-gray-100">
            <Heading className="">
              <AriaButton
                className="flex w-full flex-row items-center justify-between p-4 py-4"
                slot="trigger"
              >
                <div className="text-lg font-bold">外部連携先</div>
                <svg
                  width="16"
                  height="17"
                >
                  <g>
                    <path
                      d="M8.0208 11.9886L2.03662 5.92192L2.70153 5.25525L8.0208 10.5886L13.3401 5.25525L14.005 5.92192L8.0208 11.9886Z"
                      fill="#1A1A1A"
                    />
                  </g>
                </svg>
              </AriaButton>
            </Heading>
            <DisclosurePanel>
              <ExternalContractsForm
                child={child}
                register={register}
                control={control}
              />
            </DisclosurePanel>
          </Disclosure>
          <Disclosure className="h-full rounded-md bg-gray-100">
            <Heading className="">
              <AriaButton
                className="flex w-full flex-row items-center justify-between p-4 py-4"
                slot="trigger"
              >
                <div className="text-lg font-bold">受給者証情報</div>
                <svg
                  width="16"
                  height="17"
                >
                  <g>
                    <path
                      d="M8.0208 11.9886L2.03662 5.92192L2.70153 5.25525L8.0208 10.5886L13.3401 5.25525L14.005 5.92192L8.0208 11.9886Z"
                      fill="#1A1A1A"
                    />
                  </g>
                </svg>
              </AriaButton>
            </Heading>
            <DisclosurePanel>
              <RecipientCertificate
                child={child}
                errors={errors}
                register={register}
                control={control}
                setSealRegistrationFile={setSealRegistrationFile}
                recipientCertificateFile1={recipientCertificateFile1}
                setRecipientCertificateFile1={setRecipientCertificateFile1}
                recipientCertificateFile2={recipientCertificateFile2}
                setRecipientCertificateFile2={setRecipientCertificateFile2}
                setRecipientCertificateIsDeleted1={setRecipientCertificateIsDeleted1}
                setRecipientCertificateIsDeleted2={setRecipientCertificateIsDeleted2}
                recipientCertificateIsLocked1={recipientCertificateIsLocked1}
                recipientCertificateIsLocked2={recipientCertificateIsLocked2}
              />
            </DisclosurePanel>
          </Disclosure>
          <Disclosure className="h-full rounded-md bg-gray-100">
            <Heading className="">
              <AriaButton
                className="flex w-full flex-row items-center justify-between p-4 py-4"
                slot="trigger"
              >
                <div className="text-lg font-bold">その他</div>
                <svg
                  width="16"
                  height="17"
                >
                  <g>
                    <path
                      d="M8.0208 11.9886L2.03662 5.92192L2.70153 5.25525L8.0208 10.5886L13.3401 5.25525L14.005 5.92192L8.0208 11.9886Z"
                      fill="#1A1A1A"
                    />
                  </g>
                </svg>
              </AriaButton>
            </Heading>
            <DisclosurePanel>
              <OtherForm
                child={child}
                register={register}
                setRehabilitationCertificateFile={setRehabilitationCertificateFile}
              />
            </DisclosurePanel>
          </Disclosure>
          <Button
            className="w-full"
            variant="solid-fill"
            size="lg"
            type="submit"
            disabled={!isValid}
          >
            保存
          </Button>
          <Button
            className="w-full"
            variant="text"
            size="lg"
          >
            戻る
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserPage;

function childToFormData(child: Child): UpdateChildFormData {
  const formData: UpdateChildFormData = {
    contractStartDate: new Date(child.contract_start_date) || new Date(),
    contractEndDate: child.contract_end_date ? new Date(child.contract_end_date) : undefined,
    lastName: child.last_name,
    firstName: child.first_name,
    lastNameKana: child.last_name_kana,
    firstNameKana: child.first_name_kana,
    birthDate: child.birth_date ? new Date(child.birth_date) : new Date(),
    guardianId1: child.guardians[0]?.id || 0,
    guardianRelationship1: validateRelationShip(child.guardians[0]?.relation_ship),
    email: child.email,
    phoneNumber: child.phone_number,
    postalCode: child.postal_code || '',
    prefecture: child.prefecture || '',
    city: child.city || '',
    address1: child.address1 || '',
    address2: child.address2,
    emergencyContactRelation: validateRelationShip(child.emergency_contact_relation || ''),
    emergencyContactPhone: child.emergency_contact_phone || '',
    schoolId: schools.find((school) => school.name === child.school_name)?.id || 0,
    schoolPhoneNumber: child.school_phone_number,
    schoolGrade: child.school_grade || '',
    hasUpperManagement: child.has_upper_management || false,
    recipientCertificateNumber: child.recipient_certificate_number || 0,
    recipientCertificateStartDate1:
      (child.recipient_certificates &&
        child.recipient_certificates[0].start_date &&
        new Date(child.recipient_certificates[0].start_date)) ||
      new Date(),
    recipientCertificateEndDate1:
      (child.recipient_certificates &&
        child.recipient_certificates[0].end_date &&
        new Date(child.recipient_certificates[0].end_date)) ||
      new Date(),
    supportType: child.support_type || '',
    supportDays: child.support_days || '',
    maxMonthlyBurdenAmount: child.max_monthly_burden_amount || 0,
    recipientCertificateNotes: child.recipient_certificate_notes || '',
    recipientCertificateGuardianName: child.recipient_certificate_guardian_name || '',
    recipientCertificateGuardianBirthDate:
      (child.recipient_certificate_guardian_birth_date &&
        new Date(child.recipient_certificate_guardian_birth_date)) ||
      new Date(),
    disabilityDetails: child.disability_details || '',
    allergies: child.allergies || '',
    trainingLocation: child.training_location || '',
    notes: child.notes || '',
  };
  // 保護者情報
  if (child.guardians.length > 1) {
    formData.guardianId2 = child.guardians[1].id;
    formData.guardianRelationship2 = validateRelationShip(child.guardians[1].relation_ship);
  }
  if (child.guardians.length > 2) {
    formData.guardianId3 = child.guardians[2].id;
    formData.guardianRelationship3 = validateRelationShip(child.guardians[2].relation_ship);
  }
  if (child.guardians.length > 3) {
    formData.guardianId4 = child.guardians[3].id;
    formData.guardianRelationship4 = validateRelationShip(child.guardians[3].relation_ship);
  }
  // かかりつけ医情報
  if (child.doctors && child.doctors.length > 0) {
    formData.doctorClinicName1 = child.doctors[0].clinic_name;
    formData.doctorName1 = child.doctors[0].doctor_name;
    formData.doctorPhoneNumber1 = child.doctors[0].phone_number;
  }
  if (child.doctors && child.doctors.length > 1) {
    formData.doctorClinicName2 = child.doctors[1].clinic_name;
    formData.doctorName2 = child.doctors[1].doctor_name;
    formData.doctorPhoneNumber2 = child.doctors[1].phone_number;
  }
  if (child.doctors && child.doctors.length > 2) {
    formData.doctorClinicName3 = child.doctors[2].clinic_name;
    formData.doctorName3 = child.doctors[2].doctor_name;
    formData.doctorPhoneNumber3 = child.doctors[2].phone_number;
  }

  if (child.other_facilities && child.other_facilities.length > 0) {
    formData.otherFacilityName1 = child.other_facilities[0].name;
    formData.otherFacilityCity1 = child.other_facilities[0].city;
  }
  if (child.other_facilities && child.other_facilities.length > 1) {
    formData.otherFacilityName2 = child.other_facilities[1].name;
    formData.otherFacilityCity2 = child.other_facilities[1].city;
  }

  if (child.recipient_certificates && child.recipient_certificates.length > 1) {
    formData.recipientCertificateStartDate2 = child.recipient_certificates[1].start_date
      ? new Date(child.recipient_certificates[1].start_date)
      : new Date();
    formData.recipientCertificateEndDate2 = child.recipient_certificates[1].end_date
      ? new Date(child.recipient_certificates[1].end_date)
      : new Date();
  }

  return formData;
}
